if (!customElements.get('td-multi-offer')) {
  class TdMultiOffer extends HTMLElement {
    constructor() {
      super();
      this.skipCart = false;
      this.sectionId = '';
      this.currentTabId = null;
      this.galleryContainer = null;
      this.tabPanel = null;
      this.galleryCache = null;
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.currentProductData = null;
      this.pendingSlideIndex = null;
      this.tabState = new Map();
    }

    connectedCallback() {
      this.skipCart = this.dataset.skipCart === 'true';
      this.sectionId = this.dataset.sectionId;
      this.currentTabId = this.dataset.activeTab || 'tab-1';
      this.galleryContainer = this.querySelector('[data-gallery-container]');
      this.tabPanel = this.querySelector('[data-tab-panel]');

      const initialTabNode = this.getTabContentNode(this.currentTabId);
      if (!initialTabNode) {
        const firstTabTrigger = this.querySelector('[data-mo-tab-trigger]');
        this.currentTabId = firstTabTrigger?.dataset.tabId || this.currentTabId;
      }

      this.setupEventListeners();
      this.activateTab(this.currentTabId);
      this.syncNavState();
      this.initializeFromCurrentTab();
      this.captureCurrentTabState();
    }

    setupEventListeners() {
      this.addEventListener('click', this.handleTabClick.bind(this));
      this.addEventListener('variant:changed', this.handleVariantChanged.bind(this));
      this.addEventListener('change', this.handleInputChange.bind(this));
      this.addEventListener('submit', this.handleProductFormSubmit.bind(this));
    }

    async handleProductFormSubmit(event) {
      const form = event.target;
      const submitter = event.submitter || document.activeElement;

      if (!(form instanceof HTMLFormElement) || !submitter) return;
      if (!submitter.classList.contains('td-product-form-action__button')) return;

      event.preventDefault();

      if (form.dataset.tdSubmitting === 'true') return;
      form.dataset.tdSubmitting = 'true';

      submitter.setAttribute('disabled', 'disabled');
      submitter.setAttribute('aria-busy', 'true');

      try {
        const productForm = new FormData(form);
        productForm.append('sections', 'mini-cart');
        productForm.delete('option1');
        productForm.delete('option2');
        productForm.delete('option3');

        const customCampaign = document.body.dataset.campaign || false;
        if (customCampaign) {
          productForm.append('properties[_custom_campaign]', customCampaign);
        }

        const addUrl = `${window.themeVariables?.routes?.cartAddUrl || '/cart/add'}.js`;
        const addResponse = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: productForm
        });

        const addResponseJson = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(addResponseJson?.description || 'Add to cart failed');
        }

        if (this.skipCart) {
          window.location.href = '/checkout';
          return;
        }

        const cartResponse = await fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`);
        const cartContent = await cartResponse.json();

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartContent }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: addResponseJson,
              openMiniCart: true
            }
          })
        );
      } catch (error) {
        console.error('[TD Multi Offer] Add to cart failed:', error);
      } finally {
        submitter.removeAttribute('disabled');
        submitter.removeAttribute('aria-busy');
        form.dataset.tdSubmitting = 'false';
      }
    }

    handleTabClick(event) {
      const trigger = event.target.closest('[data-mo-tab-trigger]');
      if (!trigger) return;

      const tabId = trigger.dataset.tabId;
      if (!tabId || tabId === this.currentTabId) return;

      this.loadTab(tabId);
    }

    loadTab(tabId) {
      if (!this.tabPanel) return;
      this.captureCurrentTabState();
      this.activateTab(tabId);
      this.ensureTabPillsInView();
    }

    getTabContentNode(tabId) {
      if (!this.tabPanel || !tabId) return null;
      return this.tabPanel.querySelector(`[data-tab-content="${tabId}"]`);
    }

    activateTab(tabId) {
      if (!this.tabPanel || !tabId) return;

      const nextTabNode = this.getTabContentNode(tabId);
      if (!nextTabNode) return;

      const tabNodes = this.tabPanel.querySelectorAll('[data-tab-content]');
      tabNodes.forEach((node) => {
        const isActive = node.dataset.tabContent === tabId;
        node.classList.toggle('active', isActive);
        node.hidden = !isActive;
      });

      this.currentTabId = tabId;
      this.syncNavState();
      this.initializeFromCurrentTab(nextTabNode);
      this.restoreTabState(tabId);
      this.syncActiveTabSellingPlanPickers();
    }

    ensureTabPillsInView() {
      const pills = this.querySelector('.td-mo__pills');
      if (!(pills instanceof HTMLElement)) return;

      this.scrollPillsIntoView(pills, true);
      window.setTimeout(() => {
        this.scrollPillsIntoView(pills, false);
      }, 260);
    }

    scrollPillsIntoView(pills, allowSmooth) {
      if (!(pills instanceof HTMLElement)) return;

      const stickyNavOffset = this.getStickyNavOffset();
      const topBoundary = stickyNavOffset + 8;
      const bottomBoundary = window.innerHeight - 8;
      const rect = pills.getBoundingClientRect();
      const isFullyInView = rect.top >= topBoundary && rect.bottom <= bottomBoundary;
      if (isFullyInView) return;

      let targetTop = window.scrollY;
      if (rect.top < topBoundary) {
        targetTop += rect.top - topBoundary;
      } else if (rect.bottom > bottomBoundary) {
        targetTop += rect.bottom - bottomBoundary;
      }

      targetTop = Math.max(targetTop, 0);
      if (Math.abs(targetTop - window.scrollY) < 1) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = allowSmooth && !prefersReducedMotion ? 'smooth' : 'auto';
      window.scrollTo({ top: targetTop, behavior });
    }

    getStickyNavOffset() {
      const rootStyles = window.getComputedStyle(document.documentElement);
      const toNumber = (value) => Number.parseFloat(value) || 0;
      const stickyHeaderHeight = (
        toNumber(rootStyles.getPropertyValue('--header-height-without-bottom-nav'))
        || toNumber(rootStyles.getPropertyValue('--header-height'))
      ) * toNumber(rootStyles.getPropertyValue('--enable-sticky-header'));
      const stickyAnnouncementHeight = toNumber(rootStyles.getPropertyValue('--announcement-bar-height'))
        * toNumber(rootStyles.getPropertyValue('--enable-sticky-announcement-bar'));
      const fallbackOffset = stickyHeaderHeight + stickyAnnouncementHeight;
      const sampleX = [
        Math.round(window.innerWidth * 0.2),
        Math.round(window.innerWidth * 0.5),
        Math.round(window.innerWidth * 0.8)
      ];

      const topOverlayOffset = sampleX.reduce((maxOffset, x) => {
        const stack = document.elementsFromPoint(x, 1);
        stack.forEach((element) => {
          if (!(element instanceof HTMLElement) || element === document.documentElement || element === document.body) return;

          const styles = window.getComputedStyle(element);
          if (styles.position !== 'sticky' && styles.position !== 'fixed') return;

          const rect = element.getBoundingClientRect();
          if (rect.top > 8 || rect.bottom <= 0) return;

          maxOffset = Math.max(maxOffset, rect.bottom);
        });

        return maxOffset;
      }, 0);

      return Math.max(topOverlayOffset, fallbackOffset);
    }

    syncNavState() {
      const triggers = this.querySelectorAll('[data-mo-tab-trigger]');
      let activeIndex = 0;
      triggers.forEach((trigger, index) => {
        const isActive = trigger.dataset.tabId === this.currentTabId;
        trigger.classList.toggle('active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          activeIndex = index;
        }
      });

      const pills = this.querySelector('.td-mo__pills');
      if (pills) {
        pills.style.setProperty('--td-mo-pill-index', String(activeIndex));
      }
    }

    initializeFromCurrentTab(tabContent = null) {
      this.currentProductData = this.getCurrentTabProductData(tabContent);
      this.syncVariantPickerFromCurrentVariant(tabContent);
      this.updateGalleryForCurrentTab(tabContent);
      this.autoSelectFirstOffer();
      const didApplyVariantFilter = this.applyCurrentVariantFilter();
      if (!didApplyVariantFilter) {
        this.initializeSwiper();
      }
    }

    syncVariantPickerFromCurrentVariant(tabContent = null) {
      let currentTabContent = tabContent;
      if (!currentTabContent) {
        currentTabContent = this.getCurrentTabContent();
      }
      if (!currentTabContent || !this.currentProductData?.variants?.length) return;

      const addToCartButton = currentTabContent.querySelector('[data-mo-add-to-cart]');
      const variantId = addToCartButton?.dataset.variantId ? String(addToCartButton.dataset.variantId) : '';
      if (!variantId) return;

      const variant = this.currentProductData.variants.find((item) => String(item.id) === variantId);
      if (!variant?.options?.length) return;

      const variantPicker = currentTabContent.querySelector('[data-variant-picker]');
      if (!variantPicker) return;

      const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));
      let changed = false;

      fieldsets.forEach((fieldset, index) => {
        const desiredValue = String(variant.options[index] || '');
        if (!desiredValue) return;

        const desiredInput = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
          .find((input) => String(input.value) === desiredValue);

        if (desiredInput && !desiredInput.checked) {
          desiredInput.checked = true;
          changed = true;
        }
      });

      if (changed) {
        variantPicker.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateGalleryForCurrentTab(tabContent = null) {
      if (!this.galleryContainer) return;

      let currentTabContent = tabContent;
      if (!currentTabContent) {
        currentTabContent = this.tabPanel?.querySelector(`[data-tab-content="${this.currentTabId}"]`);
      }
      if (!currentTabContent) {
        currentTabContent = this.tabPanel?.querySelector('[data-tab-content]');
      }
      if (!currentTabContent) return;

      const tabProductId = currentTabContent.querySelector('.td-mo-tab')?.dataset.productId || '';
      const galleryTemplates = Array.from(currentTabContent.querySelectorAll('template[data-tab-gallery]'));
      let galleryTemplate = null;

      if (tabProductId) {
        galleryTemplate = galleryTemplates.find(
          (template) => String(template.dataset.galleryProductId || '') === String(tabProductId)
        );
      }

      if (!galleryTemplate) {
        galleryTemplate = galleryTemplates[0] || null;
      }

      if (!galleryTemplate) {
        this.galleryContainer.innerHTML = '';
        this.galleryCache = null;
        return;
      }

      this.destroySwipers();
      this.galleryCache = null;
      this.galleryContainer.replaceChildren(galleryTemplate.content.cloneNode(true));
      this.cacheGallerySlides();
    }

    destroySwipers() {
      if (this.mainSwiper && typeof this.mainSwiper.destroy === 'function') {
        this.mainSwiper.destroy(true, true);
      }
      if (this.thumbsSwiper && typeof this.thumbsSwiper.destroy === 'function') {
        this.thumbsSwiper.destroy(true, true);
      }
      this.mainSwiper = null;
      this.thumbsSwiper = null;
    }

    getGalleryElements() {
      const swiperSlider = this.galleryContainer?.querySelector('swiper-slider');
      if (!swiperSlider) return null;

      const mainContainer = swiperSlider.querySelector('[data-swiper]:not([data-swiper-thumbs])');
      const mainWrapper = mainContainer?.querySelector('.swiper-wrapper');
      const thumbsContainer = swiperSlider.querySelector('[data-swiper-thumbs]');
      const thumbWrapper = thumbsContainer?.querySelector('.swiper-wrapper');

      return {
        swiperSlider,
        mainContainer,
        mainWrapper,
        thumbsContainer,
        thumbWrapper
      };
    }

    cacheGallerySlides() {
      const elements = this.getGalleryElements();
      if (!elements?.mainWrapper) return;

      const mainSlides = Array.from(elements.mainWrapper.children);
      const thumbSlides = elements.thumbWrapper ? Array.from(elements.thumbWrapper.children) : [];

      this.galleryCache = {
        main: mainSlides.map((slide) => slide.cloneNode(true)),
        thumbs: thumbSlides.map((slide) => slide.cloneNode(true))
      };
    }

    initializeSwiper(initialSlide = 0) {
      this.pendingSlideIndex = Number.isFinite(initialSlide) ? Math.max(initialSlide, 0) : 0;

      requestAnimationFrame(() => {
        const elements = this.getGalleryElements();
        if (!elements?.swiperSlider || !elements.mainContainer || !window.Swiper) return;

        const slides = elements.mainContainer.querySelectorAll('[data-swiper-slide]');
        if (!slides.length) return;

        if (this.mainSwiper && typeof this.mainSwiper.destroy === 'function') {
          this.mainSwiper.destroy(true, true);
        }

        if (this.thumbsSwiper && typeof this.thumbsSwiper.destroy === 'function') {
          this.thumbsSwiper.destroy(true, true);
        }

        let sliderOptions = {};
        try {
          sliderOptions = JSON.parse(elements.swiperSlider.dataset.swiperOptions || '{}');
        } catch (error) {
          sliderOptions = {};
        }

        sliderOptions.loop = false;
        sliderOptions.speed = 320;
        sliderOptions.initialSlide = this.pendingSlideIndex;

        const thumbsSelector = elements.swiperSlider.dataset.swiperThumbs;
        if (thumbsSelector && elements.thumbsContainer) {
          let thumbOptions = {};
          try {
            thumbOptions = JSON.parse(elements.thumbsContainer.dataset.swiperOptions || '{}');
          } catch (error) {
            thumbOptions = {};
          }

          // Keep thumb active state in sync with the main slider after dynamic filtering/rebuilds.
          thumbOptions.watchSlidesProgress = true;
          thumbOptions.slideToClickedSlide = true;

          const thumbSlides = elements.thumbsContainer.querySelectorAll('[data-swiper-slide]');
          if (thumbSlides.length) {
            this.thumbsSwiper = new Swiper(elements.thumbsContainer, thumbOptions);
            sliderOptions.thumbs = { swiper: this.thumbsSwiper };
          }
        }

        this.mainSwiper = new Swiper(elements.mainContainer, sliderOptions);

        if (this.mainSwiper && Number.isFinite(this.pendingSlideIndex)) {
          const slideIndex = Math.max(this.pendingSlideIndex, 0);
          if (typeof this.mainSwiper.slideTo === 'function') {
            this.mainSwiper.slideTo(slideIndex, 180);
          }
          if (this.thumbsSwiper && typeof this.thumbsSwiper.slideTo === 'function') {
            this.thumbsSwiper.slideTo(slideIndex, 180);
          }

          this.syncActiveSlideState(slideIndex);
        }

        if (this.mainSwiper && typeof this.mainSwiper.on === 'function') {
          this.mainSwiper.on('slideChange', () => {
            this.syncActiveSlideState(this.mainSwiper.activeIndex);
            this.captureCurrentTabState();
          });
        }
      });
    }

    setVariantTransitionState(isActive) {
      const elements = this.getGalleryElements();
      if (!elements?.swiperSlider) return;
      elements.swiperSlider.classList.toggle('is-variant-transitioning', Boolean(isActive));
    }

    syncActiveSlideState(activeIndex = 0) {
      const elements = this.getGalleryElements();
      if (!elements) return;

      const mainSlides = elements.mainWrapper ? Array.from(elements.mainWrapper.children) : [];
      const thumbSlides = elements.thumbWrapper ? Array.from(elements.thumbWrapper.children) : [];
      const safeIndex = Math.max(Math.min(Number(activeIndex) || 0, Math.max(mainSlides.length - 1, 0)), 0);

      mainSlides.forEach((slide, index) => {
        slide.classList.toggle('is-selected', index === safeIndex);
      });

      thumbSlides.forEach((slide, index) => {
        slide.classList.toggle('swiper-slide-thumb-active', index === safeIndex);
      });
    }

    autoSelectFirstOffer() {
      const currentTabContent = this.getCurrentTabContent();
      if (!currentTabContent) return;

      const firstOfferInput = currentTabContent.querySelector('input[data-mo-offer]');
      if (firstOfferInput && !firstOfferInput.checked) {
        firstOfferInput.checked = true;
        firstOfferInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    applyCurrentVariantFilter() {
      const currentTabContent = this.getCurrentTabContent();
      if (!currentTabContent) return false;

      const addToCartButton = currentTabContent.querySelector('[data-mo-add-to-cart]');
      const hiddenVariantInput = currentTabContent.querySelector('input[name="id"]');
      const variantId = addToCartButton?.dataset.variantId || hiddenVariantInput?.value;

      if (!this.currentProductData || !variantId) return false;

      const variant = (this.currentProductData.variants || []).find((item) => String(item.id) === String(variantId));
      if (variant) {
        this.applyVariantGalleryFilter(variant);
        return true;
      }
      return false;
    }

    getCurrentTabProductData(tabContent = null) {
      let currentTabContent = tabContent;
      if (!currentTabContent) {
        currentTabContent = this.tabPanel?.querySelector(`[data-tab-content="${this.currentTabId}"]`);
      }
      if (!currentTabContent) {
        currentTabContent = this.tabPanel?.querySelector('[data-tab-content]');
      }
      if (!currentTabContent) return null;

      const productDataScript = currentTabContent.querySelector('.td-mo-tab__product-data');
      if (!productDataScript) return null;

      try {
        return JSON.parse(productDataScript.textContent || '{}');
      } catch (error) {
        console.warn('[TD Multi Offer] Could not parse product data for variant gallery filter', error);
        return null;
      }
    }

    extractVariantMediaIds(variant) {
      const candidates = [
        variant?.featured_media?.id,
        variant?.featured_media?.preview_image?.id,
        variant?.featured_image?.id,
        variant?.image_id,
        variant?.image?.id
      ];

      const ids = candidates
        .filter((candidate) => candidate !== undefined && candidate !== null && String(candidate) !== '')
        .map((candidate) => String(candidate));

      return Array.from(new Set(ids));
    }

    getSlideIds(slide) {
      if (!slide) return [];

      const ids = [
        slide.dataset.mediaId,
        slide.dataset.imageId
      ]
        .filter((value) => value !== undefined && value !== null && String(value) !== '')
        .map((value) => String(value));

      return Array.from(new Set(ids));
    }

    slideMatchesVariantMedia(slide, variantMediaIds) {
      if (!slide || !variantMediaIds?.size) return false;

      const slideIds = this.getSlideIds(slide);
      return slideIds.some((id) => variantMediaIds.has(id));
    }

    applyVariantGalleryFilter(variant) {
      if (!this.galleryCache || !this.galleryCache.main?.length) return;
      if (!this.currentProductData) return;
      this.setVariantTransitionState(true);

      const filteredMediaIds = [];
      const variantMediaIds = new Set(this.extractVariantMediaIds(variant));
      const productMedia = Array.isArray(this.currentProductData.media) ? this.currentProductData.media : [];

      if (productMedia.length) {
        productMedia.forEach((media) => {
          const mediaId = String(media?.id || '');
          const mediaAlt = String(media?.alt || '');
          if (!mediaId || !mediaAlt.includes('#') || variantMediaIds.has(mediaId)) return;

          const altParts = mediaAlt.split('#');
          for (let i = 1; i < altParts.length; i++) {
            const mediaGroupParts = altParts[i].split('_');
            const mediaOptionName = String(mediaGroupParts.shift() || '').trim().toLowerCase();
            const mediaOptionValue = String(mediaGroupParts.join('_') || '').trim().toLowerCase();
            if (!mediaOptionName || !mediaOptionValue) continue;

            (this.currentProductData.options || []).forEach((option) => {
              const optionName = String(option?.name || '').trim().toLowerCase();
              const optionPosition = Number(option?.position || 0);
              const variantOptionValue = String((variant?.options || [])[optionPosition - 1] || '').trim().toLowerCase();

              if (optionName === mediaOptionName && variantOptionValue !== mediaOptionValue) {
                filteredMediaIds.push(mediaId);
              }
            });
          }
        });
      } else {
        this.galleryCache.main.forEach((slide) => {
          const mediaId = String(slide.dataset.mediaId || slide.dataset.imageId || '');
          const mediaAlt = String(slide.dataset.imageAlt || '');
          const matchesFeaturedMedia = this.slideMatchesVariantMedia(slide, variantMediaIds);

          if (!mediaId || !mediaAlt.includes('#') || matchesFeaturedMedia) return;

          const altParts = mediaAlt.split('#');
          for (let i = 1; i < altParts.length; i++) {
            const mediaGroupParts = altParts[i].split('_');
            const mediaOptionName = String(mediaGroupParts.shift() || '').trim().toLowerCase();
            const mediaOptionValue = String(mediaGroupParts.join('_') || '').trim().toLowerCase();
            if (!mediaOptionName || !mediaOptionValue) continue;

            (this.currentProductData.options || []).forEach((option) => {
              const optionName = String(option?.name || '').trim().toLowerCase();
              const optionPosition = Number(option?.position || 0);
              const variantOptionValue = String((variant?.options || [])[optionPosition - 1] || '').trim().toLowerCase();

              if (optionName === mediaOptionName && variantOptionValue !== mediaOptionValue) {
                filteredMediaIds.push(mediaId);
              }
            });
          }
        });
      }

      const blockedMediaIds = new Set(filteredMediaIds);
      let filteredMainSlides = this.galleryCache.main.filter((slide) => {
        const slideMediaId = String(slide.dataset.mediaId || slide.dataset.imageId || '');
        return !blockedMediaIds.has(slideMediaId);
      });
      if (!filteredMainSlides.length) filteredMainSlides = [...this.galleryCache.main];

      const visibleMediaIds = new Set(
        filteredMainSlides
          .map((slide) => String(slide.dataset.mediaId || slide.dataset.imageId || ''))
          .filter(Boolean)
      );

      let filteredThumbSlides = [...this.galleryCache.thumbs];
      if (visibleMediaIds.size && filteredThumbSlides.length) {
        filteredThumbSlides = filteredThumbSlides.filter((slide) => {
          const slideMediaId = String(slide.dataset.mediaId || slide.dataset.imageId || '');
          return visibleMediaIds.has(slideMediaId);
        });
      }

      if (!filteredThumbSlides.length) {
        filteredThumbSlides = [...this.galleryCache.thumbs];
      }

      const elements = this.getGalleryElements();
      if (!elements?.mainWrapper) return;

      elements.mainWrapper.replaceChildren(...filteredMainSlides.map((slide) => slide.cloneNode(true)));

      if (elements.thumbWrapper) {
        elements.thumbWrapper.replaceChildren(...filteredThumbSlides.map((slide) => slide.cloneNode(true)));
      }

      const selectedSlide = filteredMainSlides.find((slide) => this.slideMatchesVariantMedia(slide, variantMediaIds));
      const selectedSlideIndex = Math.max(
        selectedSlide ? filteredMainSlides.indexOf(selectedSlide) : 0,
        0
      );

      this.initializeSwiper(selectedSlideIndex);
      setTimeout(() => this.setVariantTransitionState(false), 220);
    }

    handleVariantChanged(event) {
      const { variant } = event.detail;
      if (!variant) return;

      this.applyVariantGalleryFilter(variant);
      this.focusVariantImage(variant);

      const offerElement = event.target.closest('.td-mo-offer');
      if (!offerElement) return;

      const offerInput = offerElement.querySelector('input[data-mo-offer]');
      if (offerInput) {
        offerInput.dataset.variantId = variant.id;
      }

      this.captureCurrentTabState();
    }

    focusVariantImage(variant) {
      const variantMediaIds = new Set(this.extractVariantMediaIds(variant));
      if (!variantMediaIds.size) return;

      const elements = this.getGalleryElements();
      const slides = elements?.mainWrapper ? Array.from(elements.mainWrapper.children) : [];
      if (!slides.length) return;

      const nextIndex = slides.findIndex((slide) => this.slideMatchesVariantMedia(slide, variantMediaIds));

      if (nextIndex < 0) return;

      this.pendingSlideIndex = nextIndex;

      if (this.mainSwiper && typeof this.mainSwiper.slideTo === 'function') {
        this.mainSwiper.slideTo(nextIndex, 250);
      }

      if (this.thumbsSwiper && typeof this.thumbsSwiper.slideTo === 'function') {
        this.thumbsSwiper.slideTo(nextIndex, 250);
      }
    }

    handleInputChange(event) {
      const target = event.target;
      if (!target) return;

      if (target.matches('input[data-mo-offer], [data-variant-picker] input, [data-variant-picker] select')) {
        this.captureCurrentTabState();
      }
    }

    getCurrentTabContent() {
      return this.tabPanel?.querySelector(`[data-tab-content="${this.currentTabId}"]`)
        || this.tabPanel?.querySelector('[data-tab-content]')
        || null;
    }

    getActiveMainSlide() {
      const elements = this.getGalleryElements();
      if (!elements?.mainWrapper) return null;

      const slides = Array.from(elements.mainWrapper.children);
      if (!slides.length) return null;

      if (this.mainSwiper && Number.isFinite(this.mainSwiper.activeIndex)) {
        return slides[this.mainSwiper.activeIndex] || slides[0] || null;
      }

      return elements.mainWrapper.querySelector('.swiper-slide-active') || slides[0] || null;
    }

    captureCurrentTabState() {
      const currentTabContent = this.getCurrentTabContent();
      if (!currentTabContent || !this.currentTabId) return;

      const addToCartButton = currentTabContent.querySelector('[data-mo-add-to-cart]');
      const variantId = addToCartButton?.dataset.variantId ? String(addToCartButton.dataset.variantId) : '';

      const selectedOffer = currentTabContent.querySelector('input[data-mo-offer]:checked');
      const offerValue = selectedOffer?.value ? String(selectedOffer.value) : '';

      const selectedOptions = [];
      const fieldsets = currentTabContent.querySelectorAll('[data-variant-picker] fieldset');
      fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) {
          selectedOptions.push(String(checked.value));
        }
      });

      const activeSlide = this.getActiveMainSlide();
      const mediaId = activeSlide?.dataset?.mediaId ? String(activeSlide.dataset.mediaId) : '';
      const imageId = activeSlide?.dataset?.imageId ? String(activeSlide.dataset.imageId) : '';

      this.tabState.set(this.currentTabId, {
        variantId,
        offerValue,
        selectedOptions,
        mediaId,
        imageId
      });
    }

    restoreSelectedOffer(tabContent, state) {
      if (!state?.offerValue) return;

      const offerInputs = Array.from(tabContent.querySelectorAll('input[data-mo-offer]'));
      const offerInput = offerInputs.find((input) => String(input.value) === String(state.offerValue));
      if (!offerInput || offerInput.checked) return;

      offerInput.checked = true;
      offerInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    restoreVariantSelection(tabContent, state) {
      const variantPicker = tabContent.querySelector('[data-variant-picker]');
      if (!variantPicker) return false;

      let optionValues = Array.isArray(state.selectedOptions) ? [...state.selectedOptions] : [];

      if (!optionValues.length && state.variantId && this.currentProductData?.variants?.length) {
        const matchedVariant = this.currentProductData.variants.find(
          (variant) => String(variant.id) === String(state.variantId)
        );

        if (matchedVariant?.options?.length) {
          optionValues = matchedVariant.options.map((option) => String(option));
        }
      }

      if (!optionValues.length) return false;

      const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));
      let restored = false;

      fieldsets.forEach((fieldset, index) => {
        const desiredValue = optionValues[index];
        if (!desiredValue) return;

        const desiredInput = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
          .find((input) => String(input.value) === String(desiredValue));

        if (desiredInput && !desiredInput.checked) {
          desiredInput.checked = true;
          restored = true;
        }
      });

      if (restored) {
        variantPicker.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return restored;
    }

    restoreGallerySelection(state) {
      if (!state) return;

      const elements = this.getGalleryElements();
      if (!elements?.mainWrapper) return;

      const slides = Array.from(elements.mainWrapper.children);
      if (!slides.length) return;

      const nextIndex = slides.findIndex((slide) => {
        const mediaId = String(slide.dataset.mediaId || '');
        const imageId = String(slide.dataset.imageId || '');
        if (state.mediaId && mediaId === String(state.mediaId)) return true;
        if (state.imageId && imageId === String(state.imageId)) return true;
        return false;
      });

      if (nextIndex < 0) return;

      this.pendingSlideIndex = nextIndex;

      if (this.mainSwiper && typeof this.mainSwiper.slideTo === 'function') {
        this.mainSwiper.slideTo(nextIndex, 0);
      }

      if (this.thumbsSwiper && typeof this.thumbsSwiper.slideTo === 'function') {
        this.thumbsSwiper.slideTo(nextIndex, 0);
      }
    }

    restoreTabState(tabId) {
      const state = this.tabState.get(tabId);
      const currentTabContent = this.getCurrentTabContent();
      if (!state || !currentTabContent) return;

      this.restoreSelectedOffer(currentTabContent, state);
      const didRestoreVariant = this.restoreVariantSelection(currentTabContent, state);

      if (!didRestoreVariant) {
        this.restoreGallerySelection(state);
      }

      this.captureCurrentTabState();
    }

    syncActiveTabSellingPlanPickers() {
      const currentTabContent = this.getCurrentTabContent();
      if (!currentTabContent) return;

      const pickers = currentTabContent.querySelectorAll('td-selling-plan-picker-block');
      pickers.forEach((picker) => {
        if (typeof picker.ensureDefaultSelection === 'function') {
          picker.ensureDefaultSelection();
        }

        if (typeof picker.syncSelection === 'function') {
          picker.syncSelection();
          return;
        }

        const selected = picker.querySelector('[data-td-selling-plan-choice]:checked');
        if (selected) {
          selected.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  customElements.define('td-multi-offer', TdMultiOffer);
}
